<template>
  <AreaPageContainer
    v-if="area"
  >
      <!-- Content wrapper with header and footer -->
      <AreaPageBody>
        <AreaHeader />

        <!-- Inner Content -->
        <div class="tw-p-10">
          <!-- Title -->
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">
              Detailed Asset Allocation
            </h2>
            <AdvisorCoreStripes />
          </div>

          <!-- Text -->
          <p class="tw-font-poppins tw-mt-10">
            {{ area.headerText }}
          </p>

          <!-- Chart -->
          <div class="tw-mt-10">
            <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
              <div
                v-for="(chart, i) in area.chartData"
                :key="i"
                class="tw-grow"
              >
                <div v-if="chart?.library === 'highcharts'">
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="chart"
                    :custom-text="chartText"
                    chart-type="pie"
                    export-image
                    @chartClick="handleChartClick"
                    @seriesPointClick="handleSeriesPointClick"
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                  />
                <!-- ChartOptionsSidebar -->
                </div>
                <ChartWrapper
                  v-else
                  canvas-class="tw-max-h-96"
                  :name="area.name"
                  :chart-config="chart"
                  @chartClick="handleChartClick"
                  @imageChanged="(data) => checkupStore.updateChart(data, area.name)"
                />
              </div>
            </div>

            <!-- Combined Table for Chart Labels and Values -->
            <table class="tw-mt-10 tw-w-full tw-border-collapse tw-border tw-border-gray-200">
              <thead>
                <tr class="tw-bg-gray-100">
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    Asset Class
                  </th>
                  <th
                    v-for="chart in area.chartData[0].series"
                    :key="chart.name"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{ chart.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(chartSection, index) in chartSections"
                  :key="index"
                >
                  <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    <span :style="{ backgroundColor: getColorForChartSection(chartSection), width: '12px', height: '12px', display: 'inline-block', marginRight: '8px' }" />
                    {{ chartSection }}
                  </td>
                  <td
                    v-for="series in area.chartData[0].series"
                    :key="series.name"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{ getPercentageForChartSection(series, chartSection) }}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ChartDetailModal will be replaced by ChartClickthroughs -->
            <ChartClickthroughs
              :click-data="chartClickData"
              :get-table-data="area.clickthroughFunction"
              :download="false"
            />
          </div>

          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
            <!-- <CheckupDisclaimer class="tw-basis-0 tw-grow tw-bg-white !tw-text-[#353a46] tw-px-0" /> -->

            <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
              <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                Methodology
              </h3>
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="short"
                class="tw-text-xs tw-font-poppins"
              />
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="long"
                class="tw-text-xs tw-font-poppins tw-hidden"
              />
            </div>
          </div>
        </div>
        <AreaFooter />
        <CheckupDisclaimer class="tw-mt-16">
          <CheckupMethodologyWrapper
            :component-name="area.methodology"
            display-type="long"
          />
        </CheckupDisclaimer>
      </AreaPageBody>

      <!-- Settings Sidebar -->
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
        </template>
        <!-- <ChartToggle :options="[{value: 'pie', label: 'Basic', icon: 'fas fa-chart-bar'}, {value: 'pieextended', label: 'Extended', icon: 'fas fa-chart-bar'}]"
          @change="updateChartType"
        /> -->
        <!-- <AIChat /> -->
      </AreaSidebar>
  </AreaPageContainer>

  <div v-else>
    <p>Loading...</p>
  </div>
</template>


<script>
import { GRADE_COLORS, getExplanation } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import AreaPageBody from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageBody.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import ExtendedStyleMethodology from '@frontend/ui/features/checkup/components/methodologies/ExtendedStyleMethodology.vue';

import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import ChartToggle from '@frontend/ui/features/checkup/components/sidebar/ChartToggle.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';


export default {
  name: 'ExtendedStyleArea',
  components: {
    AreaPageContainer,
    AreaPageBody,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    ExtendedStyleMethodology,
    HighChartsWrapper,
    AreaSidebar,
    AdvisorCoreStripes,
    IncludeInPresentationToggle,
    SpeakersNotes,
    ChartToggle,
    AIChat,
    AreaHeader,
    CheckupMethodologyWrapper,
    AreaFooter,
  },
  props: {
    isActiveTab: Boolean,
  },
  data() {
    return {
      expanded: false,
      GRADE_COLORS,
      chartClickData: null,
      activeChartType: 'pie',
      explanationUpdatePending: false,
      explanationUpdateCompleted: false,
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore),

    area() {
      return this.checkupStore.areasByName?.['Extended Style'];
    },
    chartSections() {
      const chartsections = new Set();
      this.area.chartData[0].series.forEach(series => {
        series.data.forEach(point => {
          chartsections.add(point.name);
        });
      });
      return Array.from(chartsections);
    },
    portfolioName() {
      const name = this.checkupStore.portfolio?.name;
      return name || 'Portfolio';
    },
    benchmarkName() {
      const name = this.checkupStore.benchmark?.name;
      return name || 'Benchmark';
    },
    chartText() {
      const fontSize = 14;
      const width = 300;
      const y = '120%';

      const css = {
        color: 'black',
        fontSize: `${fontSize}px`,
        fontWeight: 'bold',
        width: `${width}px`,
      };

      function getOffset(text) {
        const naturalWidth = text.length * fontSize * 0.5;
        return Math.min(naturalWidth, width) / 2;
      }

      return [
        {
          config: [
            this.portfolioName,
            '25%',
            y,
          ],
          css: {
            ...css,
            transform: `translateX(-${getOffset(this.portfolioName)}px)`,
          },
        },
        {
          config: [
            this.benchmarkName,
            '75%',
            y,
          ],
          css: {
            ...css,
            transform: `translateX(-${getOffset(this.benchmarkName)}px)`,
          },
        },
      ];
    },
  },

  watch: {
    // If you are having weird bugs where the active tab seems to be wrong or off by one, double check:
    // :is-active-tab="(i+3) === activeTab" in AutoPortfolioView.vue (the number 3 may be different)
    // 😭
    async isActiveTab() {
      if (this.isActiveTab) {
        const { name, current, proposal, grade } = this.area;
        // await this.chatStore.setContext(mockJSON);

        await this.chatStore.setContext({
          jsonData: {
            currentArea: {
              name,
              [this.checkupStore.portfolio.name]: current,
              [this.benchmarkName]: proposal,
              magnitudeOfDifference: grade,

              // Dear future developer, it would probably be better to have a chatContextData coming from the area object (for all areas).
              // That way it can be easily customized.
              // This is a band-aid to fix the chat for the Holdings Concentration tab.
              holdingsConcentrationBreakdown: name === 'Concentration Breakdown' ? this.checkupStore.portfolio.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] : undefined,
            },
            // TODO: Sort highest returns to lowest so we are giving the most important info to openai
            portfolio: {
              name: this.checkupStore.portfolio.name,
              holdings: this.checkupStore.portfolio.holdings.map(h => this.createContextFromHolding(h)),
            },
            benchmark: {
              name: this.benchmarkName,
              holdings: this.checkupStore.benchmark.holdings.map(h => this.createContextFromHolding(h)),
            },
          },
        });
      }
    },
  },
  methods: {
    getPercentageForChartSection(series, chartSection) {
      const dataPoint = series.data.find(point => point.name === chartSection);
      return dataPoint ? (dataPoint.y * 100).toFixed(0) + '%' : '0%';
    },
    getColorForChartSection(chartSection) {
      let color = '';
      this.area.chartData[0].series.forEach(series => {
        const dataPoint = series.data.find(point => point.name === chartSection);
        if (dataPoint) {
          color = dataPoint.color;
        }
      });
      return color;
    },
    makeValuesPercentages(obj) {
      if (!obj) return {};
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = `${((value || 0) * 100).toFixed(0)}%`;
        return acc;
      }, {});
    },
    createContextFromHolding(h) {
      return {
        symbol: h.ticker.symbol,
        dollars: h.dollars,
        returns: this.makeValuesPercentages(h.ticker.trailing_returns_monthly_rebalance),
        sectors: this.makeValuesPercentages(h.ticker.security_breakdown?.['Breakdown']),
        expenseRatio: `${(h.ticker.expense_ratio * 100).toFixed(2)}%`,
        styles: this.makeValuesPercentages(h.ticker.security_breakdown?.['ExtendedStyle']),
        marketCap: this.makeValuesPercentages(h.ticker.security_breakdown?.['Market Cap']),
        trailingVolatilities: this.makeValuesPercentages(h.ticker.trailing_volatilities),
      };
    },
    // Maybe make callouts in parallel

    handleChartClick(data) {
      console.log('THIS CHART CLICK TEST HERE', data);
      this.chartClickData = data;
    },
    handleSeriesPointClick(pointData) {
      console.log('Series point clicked Style:', pointData);
      this.chartClickData = pointData;
    },
    updateChartType(chartType) {
      console.log('CHART TYPE', chartType);
      this.activeChartType = chartType;
    },
    async updateExplanation() {
      if (this.explanationUpdatePending || this.explanationUpdateCompleted) return; // Hack to prevent infinite loop
      this.explanationUpdatePending = true;

      console.log('Active chart data updated. Getting new explanation...');
      const { chartData, ...areaWithoutChartData } = {
          ...this.area,
          portfolioName: this.checkupStore.portfolio.name,
          benchmarkName: this.checkupStore.benchmark.name,
      };
      // Pass the modified object to getExplanation
      const explanation = await getExplanation(areaWithoutChartData);
      console.log('New explanation:', explanation);
      this.checkupStore.updateOneAreaExplanation(this.area.name, explanation);
      this.explanationUpdatePending = false;
      this.explanationUpdateCompleted = true;

      // window.clearTimeout(this.explanationUpdateTimeout);
      // this.explanationUpdateTimeout = null;
    },
  },
};
</script>


<style scoped>
.pie-chart-table {
  margin-top: 20px;
  border-collapse: collapse;
  width: 100%;
}

.pie-chart-table th,
.pie-chart-table td {
  border: 1px solid #ddd;
  padding: 8px;
}

.pie-chart-table th {
  background-color: #f2f2f2;
  text-align: left;
}
</style>
